import {
  apiGetInspectionDetail,
  apiGetInspectionItemRecords,
  apiGetReportMachineNameInfo,
  deleteInspection,
  getInspectionPDF,
  getMachineReportDetail,
  getUser,
  updateMachineReportStatus,
} from "@app/api";
import { Button, DataDialog, DataTooltip } from "@app/components/atoms";
import {
  IconActionOverflow,
  IconDelete,
  IconEdit,
  IconInputError,
  IconInvalidUrl,
  IconLocationMachine,
  IconReportCircle,
  IconTick,
  IconUser,
} from "@app/components/icons";
import { IconMessageNew } from "@app/components/icons/IconMessage/IconMessage";
import { IconNotApplicable } from "@app/components/icons/IconNotApplicable/IconNotApplicable";
import {
  INCIDENT_REPORTS,
  INSPECTION,
  INVALID_URL,
  ItemResultType,
  ReportType,
} from "@app/constants";
import { formatNumberWithCommas, REGEX } from "@app/helpers";
import { CreateInspections } from "@app/pages/Features/MachineDetail/Components/Report/CreateReports";
import type {
  InspectionFormDetailResponse,
  LatestInspectionData,
} from "@app/pages/Features/MachineDetail/Components/Report/CreateReports/Inspection/createInspectionForm.types";
import {
  RootState,
  setIsUpdatedMachineReportResponses,
  setShowAlert,
  store,
} from "@app/store";
import { PictureAsPdfOutlined } from "@mui/icons-material";
import {
  Avatar,
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AllReportsDataDialog } from "./AllReportsDataDialog";
import { MachineReportDetailDialog } from "./MachineReportDetailDialog";
import {
  ReportDialogSignatureFooter,
  ReportLastUpdatedHeader,
} from "./ReportSignatureSection";

type InspectionReportItemsDialogProps = {
  id: any;
  machineId: string;
  machineReportId: string;
  onClose?: () => void;
};

type CreateInspectionPrefill = {
  mode?: "create" | "update";
  inspectionId?: string;
  customInspectionFormId: string;
  inspectionAt: string;
  inspectionDetail: InspectionFormDetailResponse;
  latestInspection?: LatestInspectionData | null;
  existingInspection?: LatestInspectionData | null;
};

function sortInspectionItemsByPosition<T extends { position?: number }>(
  items: T[] | Record<string, T> | null | undefined,
): T[] {
  const list = Array.isArray(items)
    ? items
    : items
      ? (Object.values(items) as T[])
      : [];
  return [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

type InspectionItem = {
  customInspectionItemId: string;
  itemCode: string | null;
  result: string | number | null;
  inspectionResultId: string;
  machineReportId: string | null;
  freeText?: string | null;
  itemName: string;
  itemDescription: string;
  resultType: string;
  itemPosition: number;
  isUpdated?: boolean;
};

type InspectionRecord = {
  timeZone: string;
  inspectionId: string;
  inspectionAt: string;
  inspectionAtLocal: string;
  inspectionMonthYearLocalAt: string;
  inspectionDayLocalAt: string;
  inspectionTimeLocalAt: string;
  lat: number | string | null;
  lng: number | string | null;
  locationAccuracy: number | string | null;
  createdByUserId: string;
  givenName: string;
  surname: string;
  fullName: string;
  timeFormat: string;
  customInspectionFormId: string;
  formName: string;
  machineId: string;
  groupId: string;
  userPictureUrl: string | null;
  signatureUrl?: string | null;
  signatureBy?: string | null;
  signatureAt?: string | null;
  lastUpdatedSignatureUrl?: string | null;
  lastUpdatedSignatureBy?: string | null;
  lastUpdatedSignatureAt?: string | null;
  items: InspectionItem[];
};

type InspectionItemsResponse = {
  data: InspectionRecord[];
};

function InspectionReportItemsSkeleton() {
  const tdStyle: React.CSSProperties = {
    padding: "8px",
    border: "1px solid #ddd",
    verticalAlign: "middle",
  };

  const tdRightStyle: React.CSSProperties = {
    ...tdStyle,
    textAlign: "center",
  };

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        marginTop: 12,
        border: "1px solid #ddd",
        tableLayout: "fixed",
      }}
    >
      <tbody>
        <tr>
          <td rowSpan={2} style={{ ...tdStyle, fontWeight: "bold" }}>
            <Skeleton variant="text" width={120} height={22} />
          </td>
          <td style={{ ...tdRightStyle, fontWeight: "bold" }}>
            <Skeleton variant="text" width={90} height={22} />
          </td>
        </tr>
        <tr>
          <td style={{ ...tdRightStyle, fontWeight: "bold" }}>
            <Skeleton variant="text" width={70} height={22} />
            <Skeleton variant="text" width={50} height={16} />
          </td>
        </tr>
        <tr>
          <td style={tdStyle}></td>
          <td style={tdRightStyle}>
            <Skeleton variant="rectangular" width={24} height={24} />
          </td>
        </tr>

        {Array.from({ length: 6 }).map((_, idx) => (
          <tr key={idx}>
            <td style={tdStyle}>
              <Skeleton variant="text" width="85%" height={18} />
            </td>
            <td style={tdRightStyle}>
              <Skeleton variant="text" width={60} height={18} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function InspectionReportItemsDialog({
  id,
  machineId,
  machineReportId,
  onClose,
}: InspectionReportItemsDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [openLocation, setOpenLocation] = useState(false);
  const [record, setRecord] = useState<
    InspectionItemsResponse["data"][0] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const getUserTimezone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const timezone = useMemo(() => getUserTimezone(), []);
  const [InspectionPDFdialogOpen, setInspectionPDFdialogOpen] = useState(false);
  const [pdfHeaderRemarks, setPdfHeaderRemarks] = useState("");
  const [pdfFooterRemarks, setPdfFooterRemarks] = useState("");
  const [pdfHeaderRemarksError, setPdfHeaderRemarksError] = useState(false);
  const [pdfFooterRemarksError, setPdfFooterRemarksError] = useState(false);
  const [selectedInspectionIdPDF, setSelectedInspectionIdPDF] = useState<
    string | null
  >(null);
  const [downloadingMap, setDownloadingMap] = useState<{
    [key: string]: boolean;
  }>({});
  const [dbMachine, setDbMachine] = useState<any>();
  const [openDialogReport, setOpenDialogReport] = useState(false);
  const [userTimeFormat, setUserTimeFormat] =
    useState<string>("yyyy/MM/dd HH:mm");
  const [localeCode, setLocaleCode] = useState("en");
  const dispatch = useDispatch();
  const [openFormReport, setOpenFormReport] = useState<string>(INSPECTION);
  const [dialogKey, setDialogKey] = useState<number>(0);
  const [selectedMachineReportId, setSelectedMachineReportId] =
    useState<string>("");

  const [pdfConfirmOpen, setPdfConfirmOpen] = useState(false);
  const [pendingPDFType, setPendingPDFType] = useState<"INSPECTION" | null>(
    null,
  );
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openFormDelete, setOpenFormDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openCreateInspection, setOpenCreateInspection] = useState(false);
  const [createInspectionPrefill, setCreateInspectionPrefill] =
    useState<CreateInspectionPrefill | null>(null);
  const overflowButtonRef = useRef<HTMLSpanElement>(null);

  const screenPermission = useSelector(
    (state: RootState) => state.screenPermission.value,
  );

  const canEditOrDeleteInspection = useMemo(() => {
    if (!record?.createdByUserId || !currentUserId) return false;
    const isOwnInspection = record.createdByUserId === currentUserId;
    if (isOwnInspection) {
      return screenPermission?.allowEditDeleteInspectionResult === true;
    }
    return screenPermission?.allowEditDeleteOthersMachineReports === true;
  }, [
    record?.createdByUserId,
    currentUserId,
    screenPermission?.allowEditDeleteInspectionResult,
    screenPermission?.allowEditDeleteOthersMachineReports,
  ]);

  const handleOpenOverflowMenu = () => {
    setMenuAnchorEl(overflowButtonRef.current);
  };

  const handleCloseOverflowMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleCloseCreateInspection = () => {
    setOpenCreateInspection(false);
    setCreateInspectionPrefill(null);
  };

  const handleEditInspection = async () => {
    handleCloseOverflowMenu();
    if (!id || !machineId || !record?.inspectionId) return;

    try {
      const response = await apiGetInspectionDetail(
        id,
        machineId,
        record.inspectionId,
      );
      const data = response?.data?.data as LatestInspectionData | undefined;
      if (!data?.inspectionFormId) {
        store.dispatch(
          setShowAlert(t("Anunexpectederroroccurredwhileprocessingthefile")),
        );
        return;
      }

      setCreateInspectionPrefill({
        mode: "update",
        inspectionId: record.inspectionId,
        customInspectionFormId: data.inspectionFormId,
        inspectionAt: data.inspectionAt || "",
        inspectionDetail: {
          inspectionFormId: data.inspectionFormId,
          name: data.name || "",
          type: data.type,
          inspectionItems: sortInspectionItemsByPosition(
            data.inspectionItems as any,
          ),
        },
        existingInspection: {
          ...data,
          inspectionItems: sortInspectionItemsByPosition(
            data.inspectionItems as any,
          ),
        },
      });
      setOpenCreateInspection(true);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        store.dispatch(
          setShowAlert(
            error.response?.data?.error?.message ||
              error.response?.data?.meta?.errorMessage ||
              t("Anunexpectederroroccurredwhileprocessingthefile"),
          ),
        );
      } else {
        store.dispatch(
          setShowAlert(t("Anunexpectederroroccurredwhileprocessingthefile")),
        );
      }
    }
  };

  const confirmDeleteInspection = async () => {
    if (!record?.inspectionId || !id || !machineId) {
      console.error("Missing required parameters to delete inspection.");
      return;
    }

    try {
      setIsDeleting(true);
      await deleteInspection(id, machineId, record.inspectionId);
      store.dispatch(setShowAlert(t("deleteinspectionsuccess")));
      dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
      setOpenFormDelete(false);
      setOpen(false);
      onClose?.();
    } catch (error) {
      console.error("Failed to delete inspection", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadClick = async (inspectionId: string) => {
    if (downloadingMap[inspectionId]) {
      console.warn(
        `Download already in progress for Inspection ID: ${inspectionId}`,
      );
      return;
    }

    if (!inspectionId || !id || !machineId) {
      console.error("Missing required parameters");
      return;
    }

    setDownloadingMap((prev) => ({ ...prev, [inspectionId]: true }));

    try {
      if (!!id && !!machineId && !!record?.formName && !!inspectionId) {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await apiGetReportMachineNameInfo(inspectionId);
        const machineName = res?.data?.data?.customerMachineName;
        const timeFormat = res?.data?.data?.timeFormat;
        const customInspectionFormName =
          res?.data?.data?.customInspectionFormName;
        const reportName = t("labelInspection");

        getInspectionPDF(
          id,
          machineId,
          inspectionId,
          userTimeZone,
          pdfHeaderRemarks,
          pdfFooterRemarks,
          machineName,
          timeFormat,
          reportName,
          record.inspectionAt,
          customInspectionFormName,
        )
          .then(() => {
            setPdfHeaderRemarks("");
            setPdfFooterRemarks("");
            store.dispatch(setShowAlert(t("PDFDownloadedSuccessfully")));
          })
          .catch((error: any) => {
            console.error("PDF download failed:", error);
          });
      } else {
        console.error(
          "Missing required parameters: groupId, machineId, inspectionFormName, or inspectionId",
        );
      }

      setSelectedInspectionIdPDF(null);
      setPdfHeaderRemarks("");
      setPdfFooterRemarks("");
      setInspectionPDFdialogOpen(false);
    } catch (error) {
      console.error(
        `Error downloading PDF for Inspection ID: ${inspectionId}`,
        error,
      );
    } finally {
      setDownloadingMap((prev) => ({ ...prev, [inspectionId]: false }));
    }
  };

  const fetchInspectionItemRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGetInspectionItemRecords(
        id,
        machineId,
        machineReportId,
        timezone,
      );
      const data = res.data?.data;
      if (Array.isArray(data) && data.length > 0) {
        setRecord(data[0]);
      } else {
        setError(t("No inspection data found."));
      }
    } catch (err) {
      console.error("Failed to get inspection item records:", err);
      setError(t("Failed to fetch inspection items."));
    } finally {
      setLoading(false);
    }
  }, [id, machineId, machineReportId, timezone, t]);

  useEffect(() => {
    if (machineReportId) {
      fetchInspectionItemRecords();
    }
  }, [machineReportId, fetchInspectionItemRecords]);

  const renderItems = () => {
    if (!record?.items?.length) return null;

    const tdRightStyle = {
      padding: "8px",
      border: "1px solid #ddd",
      textAlign: "center" as const,
      verticalAlign: "middle" as const,
    };

    return (
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginTop: 12,
          border: "1px solid #ddd",
          tableLayout: "fixed",
        }}
      >
        <tbody>
          <tr>
            <td
              rowSpan={2}
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                verticalAlign: "middle",
                fontWeight: "bold",
              }}
            >
              {t("inspectionItem")}
            </td>
            <td style={{ ...tdRightStyle, fontWeight: "bold" }}>
              {record.inspectionDayLocalAt}
            </td>
          </tr>

          <tr>
            <td style={{ ...tdRightStyle, fontWeight: "bold" }}>
              {record.inspectionTimeLocalAt?.includes(" ")
                ? (() => {
                    const [time, period] =
                      record.inspectionTimeLocalAt.split(" ");
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ fontSize: "12px" }}>{period}</span>
                        <span>{time}</span>
                      </div>
                    );
                  })()
                : record.inspectionTimeLocalAt}
            </td>
          </tr>

          <tr>
            <td></td>
            <td style={tdRightStyle}>
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedInspectionIdPDF(machineReportId ?? "");
                  setPendingPDFType("INSPECTION");
                  setPdfConfirmOpen(true);
                }}
              >
                <PictureAsPdfOutlined
                  className="icon-pdf"
                  style={{ fontSize: "24px", color: "darkblue" }}
                />
              </span>
            </td>
          </tr>

          {record.items.map((item: InspectionItem) => (
            <tr key={item.customInspectionItemId}>
              <td style={{ padding: "8px", border: "1px solid #ddd" }}>
                {item.isUpdated && (
                  <p
                    className="txt-gray-1 fs-12 mt-0 mb-0"
                    style={{
                      padding: "0 0 4px",
                      marginLeft: "-2px",
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {t("dialogReport.edited")}
                  </p>
                )}
                <DataTooltip
                  isTextPre
                  width="100%"
                  alwaysShow
                  tooltipContent={
                    <>
                      <b>{item.itemName}</b>
                      {item.itemDescription && (
                        <>
                          {"\n"}
                          {item.itemDescription}
                        </>
                      )}
                    </>
                  }
                >
                  <div
                    style={{
                      maxWidth: "100%",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.itemName}
                  </div>
                </DataTooltip>
              </td>

              <td style={tdRightStyle}>
                <DataTooltip isTextPre width="100%">
                  <div
                    style={{
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {item.result === "OK" ? (
                      <span
                        style={{
                          ...getCircleStyle(!!item.machineReportId),
                          cursor: item.machineReportId ? "pointer" : "default",
                        }}
                        onClick={() =>
                          handleClickIconReport(item.machineReportId)
                        }
                      >
                        <IconTick fillColor="#0C7040" />
                      </span>
                    ) : item.result === "NONE" ? (
                      <span
                        style={{
                          ...getCircleStyle(!!item.machineReportId),
                          cursor: item.machineReportId ? "pointer" : "default",
                        }}
                        onClick={() =>
                          handleClickIconReport(item.machineReportId)
                        }
                      >
                        {!item.machineReportId && (
                          <IconMessageNew width={24} height={24} />
                        )}
                      </span>
                    ) : item.result === ItemResultType.FREE_TEXT ? (
                      (() => {
                        const hasMachineReport = !!item.machineReportId;
                        const hasFreeText = !!item.freeText?.trim?.();

                        return hasMachineReport || hasFreeText ? (
                          <span
                            title={item.freeText?.trim() || undefined}
                            style={{
                              ...getCircleStyle(true),
                              cursor: hasMachineReport ? "pointer" : "default",
                            }}
                            onClick={() => {
                              if (hasMachineReport) {
                                handleClickIconReport(item.machineReportId);
                              }
                            }}
                          />
                        ) : (
                          <span style={getCircleStyle(false)}>
                            <IconMessageNew width={24} height={24} />
                          </span>
                        );
                      })()
                    ) : item.result === "NOT_APPLICABLE" ? (
                      <span
                        style={{
                          ...getCircleStyleNotApplicable(
                            !!item.machineReportId,
                          ),
                          cursor: item.machineReportId ? "pointer" : "default",
                        }}
                        onClick={() =>
                          handleClickIconReport(item.machineReportId)
                        }
                      >
                        <IconNotApplicable />
                      </span>
                    ) : item.result === "ANOMARY" ? (
                      <span
                        style={{
                          cursor: item.machineReportId ? "pointer" : "default",
                        }}
                        onClick={(e) => {
                          if (item.machineReportId) {
                            handleClick(e, {
                              reportedId: item.machineReportId,
                              reportType: INCIDENT_REPORTS,
                            });
                          }
                        }}
                      >
                        <IconReportCircle />
                      </span>
                    ) : item.resultType === "NUMERIC" ? (
                      formatNumberWithCommas(item.result)
                    ) : (
                      item.result
                    )}
                  </div>
                </DataTooltip>
              </td>
            </tr>
          ))}

          <tr>
            <td
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                fontWeight: "bold",
              }}
            >
              {t("tableMachineDetail.reporter")}
            </td>
            <td style={tdRightStyle}>
              <DataTooltip isTextPre width="100%">
                <div
                  title={record.fullName ?? "-"}
                  style={{
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {record.fullName ?? "-"}
                </div>
              </DataTooltip>
            </td>
          </tr>

          <tr>
            <td
              style={{
                padding: "8px",
                border: "1px solid #ddd",
                fontWeight: "bold",
              }}
            >
              {t("machineLocation")}
            </td>
            <td style={tdRightStyle}>
              <DataTooltip isTextPre width="100%">
                <div
                  style={{
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {(record.lat || record.lat === 0) &&
                    (record.lng || record.lng === 0) && (
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setRecord(record);
                          setOpenLocation(true);
                        }}
                      >
                        <IconLocationMachine />
                      </span>
                    )}
                </div>
              </DataTooltip>
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const getCircleStyle = (hasBorder = true): React.CSSProperties => {
    return {
      border: hasBorder ? "2px solid #d3d3d3" : "none",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "30px",
      height: "30px",
    };
  };

  const getCircleStyleNotApplicable = (
    hasBorder = true,
    stroke = "#D5D5D5",
  ): React.CSSProperties => {
    return {
      backgroundColor: "transparent",
      border: hasBorder ? `2px solid ${stroke}` : "none",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "30px",
      height: "30px",
    };
  };

  const handleInputPdfHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfHeaderRemarks(value);
    setPdfHeaderRemarksError(isInvalid);
  };

  const handleInputPdfFooterChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isInvalid =
      !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
      !REGEX.emojiRegex.test(value);

    setPdfFooterRemarks(value);
    setPdfFooterRemarksError(isInvalid);
  };

  const handleClickIconReport = async (machineReportId: string | null) => {
    if (!machineReportId) {
      console.warn("No machine report available for this item.");

      return;
    }

    if (id && machineId) {
      try {
        const db = await getMachineReportDetail(id, machineId, machineReportId);

        setDbMachine(db);
        setOpenDialogReport(true);
        // console.log("db", db);
        // console.log("db.freeText", db?.freeText);

        if (!db?.isRead) {
          await updateMachineReportStatus(id, machineId, machineReportId, true);

          dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
        }
      } catch (error) {
        console.error("Failed to fetch machine report details. Error:", error);
      }
    } else {
      console.error("Missing groupId or machineId");
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        let isoLocaleCode = response?.data?.data?.isoLocaleCode;
        const userId = response?.data?.data?.userId || "";

        if (!timeFormat.includes("HH") && !timeFormat.includes("mm")) {
          timeFormat += " HH:mm";
        }

        setLocaleCode(isoLocaleCode);
        setUserTimeFormat(timeFormat);
        setCurrentUserId(userId);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleClick = (_: React.MouseEvent<HTMLElement>, target: any) => {
    setSelectedMachineReportId(target.reportedId);
    setOpenFormReport(INCIDENT_REPORTS);
    setDialogKey((prev) => prev + 1);
  };

  return (
    <>
      <DataDialog
        open={InspectionPDFdialogOpen}
        setOpen={() => setInspectionPDFdialogOpen(false)}
        disableBackdropClick={true}
        title={
          <p
            style={{
              fontWeight: "bold",
              fontSize: "24px",
              marginBottom: "0px",
              marginTop: "0px",
              color: "black",
            }}
          >
            {t("PDFRemarks")}
          </p>
        }
        className="dialog-container"
      >
        <p className="txt-gray-1 fs-16">{t("pfdComments")}</p>
        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("HeaderRemarks")}
            value={pdfHeaderRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfHeaderChange}
            error={pdfHeaderRemarksError}
            helperText={pdfHeaderRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfHeaderRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfHeaderRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div className="wrapper-text-field mt-8">
          <TextField
            multiline
            rows={3}
            className="input-field"
            label={t("FooterRemarks")}
            value={pdfFooterRemarks}
            fullWidth
            inputProps={{ maxLength: 1000 }}
            onChange={handleInputPdfFooterChange}
            error={pdfFooterRemarksError}
            helperText={pdfFooterRemarksError ? t("invalidCharacters") : ""}
            InputProps={{
              endAdornment: (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  {pdfFooterRemarksError && <IconInputError />}
                </div>
              ),
            }}
          />
          {pdfFooterRemarksError && (
            <span className="error-text fs-12 ml-15 txt-red"></span>
          )}
        </div>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            paddingTop: "16px",

            zIndex: 1000,
          }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setInspectionPDFdialogOpen(false);
              setSelectedInspectionIdPDF(null);
              setPdfHeaderRemarks("");
              setPdfFooterRemarks("");
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className={`btn btn-primary ml-8`}
            onClick={() => {
              handleDownloadClick(selectedInspectionIdPDF ?? "");
            }}
            disabled={pdfHeaderRemarksError || pdfFooterRemarksError}
          >
            {t("pdfOutput")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={pdfConfirmOpen}
        disableBackdropClick={true}
        setOpen={() => setPdfConfirmOpen(false)}
        className="dialog-container"
      >
        <p className="fs-16" style={{ whiteSpace: "pre-line" }}>
          {t("pdfExpireWarning")}
        </p>

        <div
          className="flex justify-content-end group-btn-footer"
          style={{ gap: "12px" }}
        >
          <Button
            className="btn btn-no-border"
            onClick={() => {
              setPdfConfirmOpen(false);
              setPendingPDFType(null);
            }}
          >
            {t("dialogReport.cancel")}
          </Button>

          <Button
            className="btn btn-primary ml-8"
            onClick={() => {
              setPdfConfirmOpen(false);

              if (pendingPDFType === "INSPECTION") {
                setInspectionPDFdialogOpen(true);
              }
            }}
          >
            {t("ok")}
          </Button>
        </div>
      </DataDialog>

      <DataDialog
        open={open}
        onClose={() => {
          setOpen(false);
          onClose?.();
        }}
        disableBackdropClick={true}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              gap: 8,
              minWidth: 0,
            }}
          >
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <DataTooltip isTextPre width="100%">
                <span
                  title={`${record?.formName ?? ""} ${
                    record?.inspectionMonthYearLocalAt ?? ""
                  }`}
                  style={{
                    fontWeight: "bold",
                    fontSize: "24px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    marginRight: "2px",
                  }}
                >
                  {loading ? (
                    <Skeleton variant="text" width={260} height={34} />
                  ) : (
                    `${record?.formName ?? ""} (${
                      record?.inspectionMonthYearLocalAt ?? ""
                    })`
                  )}
                </span>
              </DataTooltip>
            </div>
          </div>
        }
        action={
          !loading && canEditOrDeleteInspection ? (
            <span ref={overflowButtonRef}>
              <Button
                className="bnt-action-form"
                onClick={handleOpenOverflowMenu}
              >
                <IconActionOverflow />
              </Button>
            </span>
          ) : undefined
        }
        dialogActions={
          <ReportDialogSignatureFooter
            loading={loading}
            showSignatureInFooter
            signatureUrl={record?.signatureUrl}
            signatureBy={record?.signatureBy}
            signatureAt={record?.signatureAt}
            userTimeFormat={userTimeFormat}
          >
            <Button
              onClick={() => {
                setOpen(false);
                onClose?.();
              }}
              className="btn btn-no-border"
            >
              {t("dialogRefuel.close")}
            </Button>
          </ReportDialogSignatureFooter>
        }
      >
        <div className="inspection-report-items-dialog">
          {!loading && (
            <ReportLastUpdatedHeader
              isUpdated={record?.items?.some((item) => item.isUpdated)}
              lastUpdatedSignatureUrl={record?.lastUpdatedSignatureUrl}
              lastUpdatedSignatureBy={record?.lastUpdatedSignatureBy}
              lastUpdatedSignatureAt={record?.lastUpdatedSignatureAt}
              userTimeFormat={userTimeFormat}
              marginLeft="4px"
            />
          )}
          {loading ? <InspectionReportItemsSkeleton /> : renderItems()}
        </div>
      </DataDialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseOverflowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleEditInspection}>
          <ListItemIcon>
            <IconEdit />
          </ListItemIcon>
          <ListItemText primary={t("formAction.edit")} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseOverflowMenu();
            setOpenFormDelete(true);
          }}
        >
          <ListItemIcon>
            <IconDelete />
          </ListItemIcon>
          <ListItemText
            primary={t("formAction.delete")}
            primaryTypographyProps={{ style: { color: "#DA093B" } }}
          />
        </MenuItem>
      </Menu>

      <DataDialog
        title={t("inspectionDelete.title")}
        open={openFormDelete}
        setOpen={setOpenFormDelete}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("inspectionDelete.content")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setOpenFormDelete(false)}
              className="btn btn-no-border"
              disabled={isDeleting}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={confirmDeleteInspection}
              className="btn-delete"
              disabled={isDeleting}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>

      <CreateInspections
        open={openCreateInspection}
        onClose={handleCloseCreateInspection}
        groupId={id || ""}
        machineId={machineId || ""}
        prefill={createInspectionPrefill}
        onSuccess={() => {
          dispatch(setIsUpdatedMachineReportResponses(`${new Date()}`));
          fetchInspectionItemRecords();
          handleCloseCreateInspection();
        }}
      />

      <DataDialog
        title={t("tableMachineDetail.location")}
        open={openLocation}
        setOpen={setOpenLocation}
        className="dialog-location-report"
        disableBackdropClick={true}
      >
        <iframe
          title="map-location"
          className="map-iframe"
          loading="lazy"
          src={`https://maps.google.com/maps?&q="+${record?.lat}, ${record?.lng}"&output=embed`}
        />
        <span className="fs-14 txt-gray-1 label-dialog">
          {t("tableMachineDetail.date")}
        </span>
        <p className="fs-16 txt-gray mt-4">
          {record?.inspectionAtLocal && record?.inspectionAtLocal}
          <br />
        </p>
        <span className="fs-14 txt-gray-1 label-dialog">
          {t("tableMachineDetail.reporter")}
        </span>
        <div className="flex-centered justify-content-start mt-8">
          {record?.userPictureUrl ? (
            record?.userPictureUrl === INVALID_URL ? (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconInvalidUrl />
              </Box>
            ) : (
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                }}
                alt="avatar-reporter"
                src={record?.userPictureUrl}
                imgProps={{
                  onError: () => {
                    setRecord({ ...record, userPictureUrl: INVALID_URL });
                  },
                }}
              />
            )
          ) : (
            <Box
              sx={{
                width: 24,
                height: 24,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconUser />
            </Box>
          )}

          <span className="fs-16 txt-gray ml-5 word-break txt-pre-wrap">
            {record?.fullName}
          </span>
        </div>
        <div className="flex justify-content-end">
          <Button
            onClick={() => setOpenLocation(false)}
            className="btn btn-no-border"
          >
            {t("dialogRefuel.close")}
          </Button>
        </div>
      </DataDialog>

      <MachineReportDetailDialog
        open={openDialogReport}
        onClose={() => {
          setOpenDialogReport(false);
          setDbMachine("");
        }}
        dbMachine={dbMachine}
        setDbMachine={setDbMachine}
        userTimeFormat={userTimeFormat}
        localeCode={localeCode}
      />

      {machineReportId && openFormReport !== ReportType.INSPECTION && (
        <AllReportsDataDialog
          key={dialogKey}
          id={id}
          machineId={machineId}
          machineReportId={selectedMachineReportId}
          reportType={openFormReport}
          onClose={() => {
            setSelectedMachineReportId("");
            setOpenFormReport(INSPECTION);
          }}
        />
      )}
    </>
  );
}
